import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCartService,
  addToCartService,
  updateCartService,
} from "../services/addToCartServices";
import { removeFromCartService } from "../services/removeFromCartServices";

// -----------------------------
// Async thunks
// -----------------------------

export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async ({ lang, couponCode }, thunkAPI) => {
    try {
      const response = await getCartService({ lang, couponCode });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || "Failed to fetch cart"
      );
    }
  }
);

export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ product_id, combinations }, { getState, rejectWithValue }) => {
    const { auth } = getState();
    const token = auth.api_token;
    if (!token) return;

    try {
      const response = await addToCartService({ product_id, combinations });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to add product to cart"
      );
    }
  }
);

export const updateCart = createAsyncThunk(
  "cart/updateCart",
  async ({ product_id, quantity }, { getState, rejectWithValue }) => {
    const { auth } = getState();
    const token = auth.api_token;
    if (!token) return;

    try {
      const response = await updateCartService(product_id, quantity);
      return { response: response.data, product_id, quantity };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update product quantity"
      );
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async (product_id, { getState, rejectWithValue }) => {
    const { auth } = getState();
    const token = auth.api_token;

    if (token) {
      try {
        await removeFromCartService(product_id);
        return product_id;
      } catch (error) {
        return rejectWithValue(
          error.response?.data || "Failed to remove item from cart"
        );
      }
    } else {
      let cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
      cartItems = cartItems.filter((item) => item.product_id !== product_id);
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      return product_id;
    }
  }
);

// -----------------------------
// Slice
// -----------------------------

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [],
    totalAmount: 0,
    deliveryCharges: 0,
    couponDiscount: 0,
    netAmount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    loadCartFromLocalStorage: (state) => {
      state.cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
    },
    clearCart: (state) => {
      localStorage.removeItem("cartItems");
      state.cartItems = [];
    },
    updateCartQuantity: (state, action) => {
      const { product_id, quantity } = action.payload;
      const item = state.cartItems.find((it) => it.product_id === product_id);
      if (item) item.quantity = quantity;
    },
    removeCartLocally: (state, action) => {
      const product_id = action.payload;
      state.cartItems = state.cartItems.filter(
        (item) => item.product_id !== product_id
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // -----------------------------
      // Fetch Cart
      // -----------------------------
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload || {};
        state.cartItems = data.items || [];
        state.totalAmount = data.total_amount || 0;
        state.deliveryCharges = data.delivery_charges || 0;
        state.couponDiscount = data.coupon_discount || 0;
        state.netAmount = data.net_amount || 0;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -----------------------------
      // Add to Cart
      // -----------------------------
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload || {};
        state.cartItems = data.items || [];
        state.totalAmount = data.total_amount || 0;
        state.deliveryCharges = data.delivery_charges || 0;
        state.couponDiscount = data.coupon_discount || 0;
        state.netAmount = data.net_amount || 0;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -----------------------------
      // Update Cart (no full re-render)
      // -----------------------------
      .addCase(updateCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCart.fulfilled, (state, action) => {
        state.loading = false;
        const { response, product_id, quantity } = action.payload;

        // ✅ Only update the changed item's quantity
        const item = state.cartItems.find((it) => it.product_id === product_id);
        if (item) item.quantity = quantity;

        // ✅ Update totals (avoid replacing array)
        if (response?.total_amount !== undefined) {
          state.totalAmount = response.total_amount;
        }
        if (response?.net_amount !== undefined) {
          state.netAmount = response.net_amount;
        }
        if (response?.delivery_charges !== undefined) {
          state.deliveryCharges = response.delivery_charges;
        }
        if (response?.coupon_discount !== undefined) {
          state.couponDiscount = response.coupon_discount;
        }
      })
      .addCase(updateCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // -----------------------------
      // Remove Item (no full re-render)
      // -----------------------------
      .addCase(removeCartItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        state.loading = false;
        const product_id = action.payload;
        state.cartItems = state.cartItems.filter(
          (item) => item.id !== product_id
        );
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  loadCartFromLocalStorage,
  clearCart,
  updateCartQuantity,
  removeCartLocally,
} = cartSlice.actions;

export default cartSlice.reducer;
